/**
 * Barras horizontales para comparar precios promedio entre productos.
 *
 * Se resuelve con `div` (sin SVG): la etiqueta puede ser larga, la barra se
 * ajusta al ancho disponible y el valor queda alineado a la derecha.
 */

import { cn } from '@/lib/utils';

export interface BarraGrafico {
  etiqueta: string;
  valor: number;
  /** Texto secundario (p. ej. `mín S/ 16.05 · máx S/ 21.45`). */
  detalle?: string;
  /** Clase Tailwind del color de la barra. */
  color?: string;
}

const COLORES = [
  'bg-brand-400',
  'bg-emerald-400',
  'bg-amber-400',
  'bg-sky-400',
  'bg-violet-400',
  'bg-rose-400',
];

export function GraficoBarras({
  barras,
  formato,
}: {
  barras: BarraGrafico[];
  formato: (valor: number) => string;
}) {
  if (barras.length === 0) {
    return <p className="text-sm text-ink-400">Sin productos en este periodo.</p>;
  }

  const maximo = Math.max(...barras.map((barra) => barra.valor), 1);

  return (
    <ul className="space-y-3">
      {barras.map((barra, indice) => (
        <li key={barra.etiqueta}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm text-ink-700" title={barra.etiqueta}>
              {barra.etiqueta}
            </span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-ink-900">
              {formato(barra.valor)}
            </span>
          </div>

          <div
            className="mt-1 h-2 w-full overflow-hidden rounded-full bg-ink-100"
            role="presentation"
          >
            <div
              className={cn(
                'h-full rounded-full transition-all',
                barra.color ?? COLORES[indice % COLORES.length],
              )}
              style={{ width: `${Math.max(2, (barra.valor / maximo) * 100)}%` }}
            />
          </div>

          {barra.detalle ? (
            <span className="mt-0.5 block text-[11px] text-ink-400">{barra.detalle}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
